// Visualization Transforms — Architecture B 일원화 utility (2026-05-19)
//
// 6 visualization 도구의 frontend transformation 로직 추출 (통계 페이지 분류 로직과 정합).
// 입력: call_api로 buffer.items에 적재된 raw 데이터 + period args
// 출력: 각 visualization Component가 props로 받는 형식
//
// 일원화 패턴:
// [Before] executeShowMemberStatus → fetchModule.getDataVersion2 → 자체 분류 → Component
// [After]  call_api(intention) → buffer.items 적재 → executeShowMemberStatus → utility(items, args) → Component
//
// 통계 페이지(/statistics)의 MemberStatusBox/AttendanceBox/LTVMainContent 분류 로직과 동일.
// backend 응답 형식 변화 시 본 utility만 수정하면 통계 페이지와 AI 도구 모두 sync.

import moment from 'moment';

// ────────────────────────────────────────────────
// classifyMemberStatus — show_member_status (신규/재등록/이탈 분류)
// ────────────────────────────────────────────────
//
// 입력 items: CHECK_MEMBER+MEMBERSHIP+HISTORY_LIST buffer.items 또는 raw member/all/statistics
//   [{ memberId, name, gender, birthDay, phone, countryCode, usages: [{ startDate, endDate }] }]
// 분류 기준:
//   - new: 산정기간 내 첫 이용권 시작 (usages[0].startDate ∈ period)
//   - reregistered: 산정기간 내 비-첫 이용권 시작 (usages[j].startDate ∈ period, j > 0)
//   - churned: 마지막 이용권 종료가 산정기간 내 + 오늘 이전 (usages[last].endDate ∈ period, today > endDate)
//
// 통계 페이지 MemberStatusBox와 동일.
export function classifyMemberStatus(items, { memberType, periodStartISO, periodEndISO }) {
  if (!Array.isArray(items) || !memberType) return [];
  const periodStart = moment(periodStartISO);
  const periodEnd = moment(periodEndISO);
  const today = moment();
  const filtered = [];

  for (const member of items) {
    if (!member || typeof member !== 'object') continue;
    const usages = Array.isArray(member.usages) ? member.usages : [];

    if (memberType === 'new' || memberType === 'reregistered') {
      for (let j = 0; j < usages.length; j++) {
        if (periodStart.isSameOrBefore(usages[j].startDate, 'day')
          && periodEnd.isSameOrAfter(usages[j].startDate, 'day')) {
          const isNew = j === 0;
          if ((memberType === 'new' && isNew) || (memberType === 'reregistered' && !isNew)) {
            filtered.push({ ...member, _matchedStartDate: usages[j].startDate });
          }
          break;
        }
      }
    }
    else if (memberType === 'churned') {
      for (let j = 0; j < usages.length; j++) {
        if (j === usages.length - 1
          && periodStart.isSameOrBefore(usages[j].endDate, 'day')
          && periodEnd.isSameOrAfter(usages[j].endDate, 'day')
          && today.isAfter(usages[j].endDate, 'day')) {
          filtered.push({ ...member, _matchedEndDate: usages[j].endDate });
          break;
        }
      }
    }
  }

  // 필드 매핑 + 정렬 (통계 페이지 동일)
  const members = filtered.map(item => ({
    memberId: item.memberId || item.userId,
    name: item.name || item.memberName || '',
    gender: item.gender || '',
    phone: item.phone || '',
    countryCode: item.countryCode || '',
    startDate: item._matchedStartDate || item.viewStartDate || item.createdDate || '',
    endDate: item._matchedEndDate || item.viewEndDate || '',
  }));

  if (memberType === 'churned') {
    members.sort((a, b) => (b.endDate || '').localeCompare(a.endDate || ''));
  }
  else {
    members.sort((a, b) => (b.startDate || '').localeCompare(a.startDate || ''));
  }
  return members;
}

// ────────────────────────────────────────────────
// computeMemberStatusRevenue — show_member_status 신규/재등록 회원 매출 총액
// ────────────────────────────────────────────────
//
// 통계 페이지 MemberStatusBox 매출 로직 미러 (MemberStatusBox.js:126-160):
//   분류된 회원(members)의 memberId 집합으로 SALES 거래를 조인 → cardPrice+cashPrice 합산.
//   통계는 "신규 분류 회원의 모든 결제"를 신규매출로 봄 → memberId 집합 조인(거래 단위 아님)으로 동일.
// 입력:
//   members: classifyMemberStatus 결과(표시 subset, memberId 포함) — 표시 목록 ↔ 매출 내부 정합
//   salesItems: CHECK_SALES_STATISTICS_BY+PERIOD buffer.items ([{ memberId, cardPrice, cashPrice }])
// 이탈(churned)은 매출 개념 부적합 → 호출측에서 new/reregistered만 호출.
export function computeMemberStatusRevenue(members, salesItems) {
  if (!Array.isArray(members) || members.length === 0) return 0;
  if (!Array.isArray(salesItems) || salesItems.length === 0) return 0;

  const memberIdSet = new Set(members.map(m => m && m.memberId).filter(id => id != null));

  let totalRevenue = 0;

  for (const s of salesItems) {
    if (!s || !memberIdSet.has(s.memberId)) continue;
    totalRevenue += (Number(s.cardPrice) || 0) + (Number(s.cashPrice) || 0);
  }

  return totalRevenue;
}

// ────────────────────────────────────────────────
// sortClassAttendance — show_class_attendance (수업별/시간대별 수강 인원)
// ────────────────────────────────────────────────
//
// 입력 items: CHECK_RESERVE+CLASS+SLOT_LIST_BY+PERIOD buffer.items
//   [{ classTime: 'HH:MM-HH:MM', lessonName, teacherNames, color, reserveCount }]
// 출력: { frequencyData (classTime asc 정렬), stats (총 예약/인기 수업/인기 시간대/평균) }
//
// 통계 페이지 ClassAttendanceList와 동일 — frontend에서 reserveCount 내림차순 재정렬.
export function sortClassAttendance(items) {
  if (!Array.isArray(items)) return { frequencyData: [], stats: null };

  const frequencyData = items.slice().sort((a, b) => {
    if (a.classTime > b.classTime) return 1;
    if (a.classTime < b.classTime) return -1;
    return 0;
  });

  let totalReserves = 0;
  let topClass = null;
  const lessonAggMap = new Map();
  const timeAggMap = new Map();

  for (const item of frequencyData) {
    totalReserves += item.reserveCount || 0;
    if (!topClass || (item.reserveCount || 0) > topClass.reserveCount) {
      topClass = {
        classTime: item.classTime,
        lessonName: item.lessonName,
        teacherNames: item.teacherNames,
        reserveCount: item.reserveCount || 0,
      };
    }
    const lessonKey = item.lessonName || '(이름없음)';
    lessonAggMap.set(lessonKey, (lessonAggMap.get(lessonKey) || 0) + (item.reserveCount || 0));
    const timeKey = item.classTime || '(미지정)';
    timeAggMap.set(timeKey, (timeAggMap.get(timeKey) || 0) + (item.reserveCount || 0));
  }

  let topLessonName = null, topLessonCount = 0;

  for (const [name, count] of lessonAggMap) {
    if (count > topLessonCount) { topLessonCount = count; topLessonName = name; }
  }
  let topTimeSlot = null, topTimeCount = 0;

  for (const [time, count] of timeAggMap) {
    if (count > topTimeCount) { topTimeCount = count; topTimeSlot = time; }
  }

  const avgReservePerClass = frequencyData.length > 0 ? Math.round(totalReserves / frequencyData.length) : 0;

  return {
    frequencyData,
    stats: { totalReserves, avgReservePerClass, topClass, topLessonName, topLessonCount, topTimeSlot, topTimeCount },
  };
}

// ────────────────────────────────────────────────
// rankMemberAttendance — show_attendance_ranking (회원별 출석 횟수 랭킹)
// ────────────────────────────────────────────────
//
// 입력:
//   attendanceItems: CHECK_MEMBER+MONTHLY+ATTENDANCE_LIST_BY+PERIOD buffer.items
//     [{ memberId, name, gender, birthDay, phone, countryCode, endDate, reserveAttendances: [{ classDate }] }]
//   memberItems: CHECK_MEMBER+MEMBERSHIP+HISTORY_LIST buffer.items (메타 보강용)
//     [{ memberId, name, ... }]
//   countOnce: 하루 1회 집계 여부 (true=중복 제외)
//
// 출처: 통계 페이지 AttendanceBox 로직 reuse. 0회 회원도 포함 (memberItems 기반 left join).
export function rankMemberAttendance({ attendanceItems, memberItems, periodStartISO, periodEndISO, countOnce = true }) {
  if (!Array.isArray(attendanceItems)) attendanceItems = [];
  if (!Array.isArray(memberItems)) memberItems = [];

  // memberId → attendanceCount Map
  const attendCountByMember = new Map();

  for (const m of attendanceItems) {
    if (!m || !m.memberId) continue;
    const attendances = Array.isArray(m.reserveAttendances) ? m.reserveAttendances : [];
    let count = 0;
    if (countOnce) {
      const seenDays = new Set();

      for (const a of attendances) {
        const day = (a.classDate || '').split('T')[0];
        if (day && !seenDays.has(day)) { seenDays.add(day); count++; }
      }
    }
    else {
      count = attendances.length;
    }
    attendCountByMember.set(m.memberId, count);
  }

  // memberItems 기반으로 0회 회원도 포함 — 통계 페이지 동작 매칭
  //   attendanceCount/memberships = AttendanceRankingList 렌더러 계약 필드(member.attendanceCount:122, member.memberships:114).
  //   attendCount만 내보내던 baseline은 표 출석횟수 칸 공백 + 표 내부 sort NaN → 운영 "출석 횟수 안나옴" 직결. 통계페이지와 동일 shape로 정합.
  const ranking = memberItems.map(m => {
    const _attend = attendCountByMember.get(m.memberId) || 0;
    const _memberships = Array.isArray(m.usages)
      ? [...new Set(m.usages.map(u => u && u.memberShipName).filter(Boolean))]
      : (m.membershipName ? [m.membershipName] : []);
    return {
      memberId: m.memberId,
      name: m.name || '',
      gender: m.gender || '',
      birthDay: m.birthDay || '',
      phone: m.phone || '',
      countryCode: m.countryCode || '',
      attendCount: _attend,           // 내부/오케스트레이션 텍스트용 (기존 호환)
      attendanceCount: _attend,       // 렌더러 표시/정렬 계약 필드
      memberships: _memberships,      // 렌더러 회원권 칼럼 계약 필드
    };
  });

  // 출석 횟수 내림차순 정렬
  ranking.sort((a, b) => b.attendCount - a.attendCount);
  ranking.forEach((m, i) => { m.rank = i + 1; });

  return { ranking, totalAttended: Array.from(attendCountByMember.values()).reduce((a, b) => a + b, 0) };
}

// ────────────────────────────────────────────────
// aggregateAttendanceChart — show_attendance_chart (출석 추이 차트)
// ────────────────────────────────────────────────
//
// 입력:
//   attendanceItems: CHECK_MEMBER+MONTHLY+ATTENDANCE_LIST_BY+PERIOD 현재 기간 buffer.items
//   prevAttendanceItems: 작년 동기간 buffer.items (monthly 모드만, optional)
//   memberItems: CHECK_MEMBER+MEMBERSHIP+HISTORY_LIST buffer.items (유효회원 산정용)
//   viewMode: 'daily' | 'monthly'
//
// 출처: 통계 페이지 AttendanceBox 로직 정합. AgentSession.executeShowAttendanceChart와 동일 산식.
//
// 핵심 metric 정의:
//   - 일별/월별 rate = (해당 일/월 출석 회원 수 / 해당 일/월 유효 회원 수) × 100 [%]
//     · 유효 회원: 해당 일/월에 회원권 기간(start ≤ day ≤ end) overlap된 회원
//   - avgAttendanceRate (평균 출석률) = (∑ 일별 rate) / dayCount (validMembers>0 인 일/월만 카운트)
//   - avgAttendanceCount (평균 출석 인원) = ∑ attendees / dayCount
//   - totalAttended = ∑ chartData[].attended
//
// 중요: 단순 `totalAttended / activeMemberCount` 같은 누적/스냅샷 계산은 % 정의 위반 (count-per-member).
//       반드시 per-day (attendees / validMembers) 평균이어야 frontend AttendanceBox와 일치.
export function aggregateAttendanceChart({ attendanceItems, prevAttendanceItems, memberItems, viewMode, periodStartISO, periodEndISO, countOnce = true, nowISO = null }) {
  if (!Array.isArray(attendanceItems)) attendanceItems = [];
  if (!Array.isArray(memberItems)) memberItems = [];
  prevAttendanceItems = Array.isArray(prevAttendanceItems) ? prevAttendanceItems : null;

  const periodStart = moment(periodStartISO);
  const periodEnd = moment(periodEndISO);
  const now = nowISO ? moment(nowISO) : moment();
  const isMonthly = viewMode === 'monthly';

  // ── per-member usages 파싱 (AttendanceBox.parsedAllMembers 정합) ──
  const parsedMembers = memberItems.map(m => ({
    memberId: m.memberId,
    usages: (Array.isArray(m.usages) ? m.usages : [])
      .filter(u => u && u.startDate && u.endDate)
      .map(u => ({
        startTs: moment(String(u.startDate).slice(0, 10)).startOf('day').valueOf(),
        endTs: moment(String(u.endDate).slice(0, 10)).startOf('day').valueOf(),
      })),
  }));

  // 해당 day timestamp에 유효한 회원 수 (overlap)
  function countValidOnDay(dayTs) {
    let count = 0;

    for (const pm of parsedMembers) {
      for (const u of pm.usages) {
        if (u.startTs <= dayTs && u.endTs >= dayTs) { count++; break; }
      }
    }
    return count;
  }

  // 해당 month (YYYY-MM)에 유효한 회원 수 (overlap)
  function countValidInMonth(yr, mon) {
    const mStartTs = moment([yr, mon - 1, 1]).startOf('day').valueOf();
    const lastDay = moment([yr, mon - 1, 1]).endOf('month').date();
    const mEndTs = moment([yr, mon - 1, lastDay]).startOf('day').valueOf();
    let count = 0;

    for (const pm of parsedMembers) {
      for (const u of pm.usages) {
        if (u.endTs >= mStartTs && u.startTs <= mEndTs) { count++; break; }
      }
    }
    return count;
  }

  // ── 출석 집계 (countOnce=true: 회원당 하루 1회) ──
  // daily 모드: day(YYYY-MM-DD) -> unique members 출석 수
  // monthly 모드: month(YYYY-MM) -> unique members 출석 수
  function aggregateBuckets(items) {
    const buckets = new Map();  // bucketKey -> Set(memberId)
    if (!Array.isArray(items)) return buckets;
    for (const m of items) {
      const attendances = Array.isArray(m.reserveAttendances) ? m.reserveAttendances : [];
      const seenPerBucket = new Set();

      for (const a of attendances) {
        if (!a.classDate) continue;
        const day = String(a.classDate).split('T')[0];
        if (!day || day.length < 10) continue;
        const bucket = isMonthly ? day.slice(0, 7) : day;
        if (countOnce) {
          const key = `${m.memberId}:${bucket}`;
          if (seenPerBucket.has(key)) continue;
          seenPerBucket.add(key);
        }
        if (!buckets.has(bucket)) buckets.set(bucket, 0);
        buckets.set(bucket, buckets.get(bucket) + 1);
      }
    }
    return buckets;
  }

  const attendByBucket = aggregateBuckets(attendanceItems);
  const prevAttendByBucket = (isMonthly && prevAttendanceItems) ? aggregateBuckets(prevAttendanceItems) : null;

  // ── chartData 생성 (per-day/per-month rate 포함) ──
  const chartData = [];
  if (isMonthly) {
    const targetYear = periodStart.year();

    for (let i = 0; i < 12; i++) {
      const monthKey = moment([targetYear, i, 1]).format('YYYY-MM');
      const attended = attendByBucket.get(monthKey) || 0;
      const validMembers = countValidInMonth(targetYear, i + 1);
      const rate = validMembers > 0 ? Math.round((attended / validMembers) * 100 * 10) / 10 : 0;
      const prevAttended = prevAttendByBucket
        ? (prevAttendByBucket.get(moment([targetYear - 1, i, 1]).format('YYYY-MM')) || 0)
        : null;
      chartData.push({ bucket: monthKey, attended, prevAttended, validMembers, rate });
    }
  }
  else {
    const cursor = periodStart.clone().startOf('day');
    const endDay = periodEnd.clone().startOf('day');
    // [rid90] daily 차트는 오늘 이후(미래) 날짜를 렌더하지 않음 — 미래는 출석 결정적 0이라
    //   호버 툴팁이 빈 점("유효회원/출석자 안나옴")이 됨. 평균(avgAttendanceRate)은 이미 미래 제외 →
    //   표시도 today로 clamp해 일관. dual sync: AgentSession.executeShowAttendanceChart daily 루프.
    const todayDay = now.clone().startOf('day');
    const renderEndDay = endDay.isBefore(todayDay) ? endDay : todayDay;

    while (cursor.isSameOrBefore(renderEndDay, 'day')) {
      const day = cursor.format('YYYY-MM-DD');
      const dayTs = cursor.valueOf();
      const attended = attendByBucket.get(day) || 0;
      const validMembers = countValidOnDay(dayTs);
      const rate = validMembers > 0 ? Math.round((attended / validMembers) * 100 * 10) / 10 : 0;
      chartData.push({ bucket: day, attended, validMembers, rate });
      cursor.add(1, 'day');
    }
  }

  // ── 평균 출석률/평균 출석 인원 (validMembers>0 + 일별: 현재 시점 이전, 월별: 현재 월 이하만) ──
  const totalAttended = chartData.reduce((a, b) => a + (b.attended || 0), 0);
  let sumRates = 0;
  let sumAttendees = 0;
  let counted = 0;

  if (isMonthly) {
    const lastMonth = now.month() + 1;  // 1~12
    const targetYear = periodStart.year();
    const cmpYear = now.year();

    for (const d of chartData) {
      const [yStr, mStr] = d.bucket.split('-');
      const yNum = parseInt(yStr, 10);
      const mNum = parseInt(mStr, 10);
      // 현재 연도면 이번달까지만, 과거 연도면 12월까지
      if (yNum === cmpYear && mNum > lastMonth) continue;
      if (d.validMembers > 0) {
        sumRates += (d.attended / d.validMembers) * 100;
        sumAttendees += d.attended;
        counted++;
      }
    }
  }
  else {
    const todayDayStart = now.clone().startOf('day').valueOf();
    const endDay = periodEnd.clone().startOf('day').valueOf();
    const limitTs = Math.min(endDay, todayDayStart);

    for (const d of chartData) {
      const dayTs = moment(d.bucket).startOf('day').valueOf();
      if (dayTs > limitTs) continue;  // 미래 미반영
      if (d.validMembers > 0) {
        sumRates += (d.attended / d.validMembers) * 100;
        sumAttendees += d.attended;
        counted++;
      }
    }
  }

  const avgAttendanceRate = counted > 0 ? Math.round((sumRates / counted) * 10) / 10 : 0;
  const avgAttendanceCount = counted > 0 ? Math.round((sumAttendees / counted) * 10) / 10 : 0;

  // ── peak / low (validMembers>0 + 현재 시점 이하만) ──
  let maxRatePoint = null;
  let minRatePoint = null;

  for (const d of chartData) {
    if (!(d.validMembers > 0)) continue;
    if (isMonthly) {
      const [yStr, mStr] = d.bucket.split('-');
      if (parseInt(yStr, 10) === now.year() && parseInt(mStr, 10) > now.month() + 1) continue;
    }
    else {
      if (moment(d.bucket).startOf('day').isAfter(now, 'day')) continue;
    }
    if (!maxRatePoint || d.rate > maxRatePoint.rate) maxRatePoint = d;
    if (!minRatePoint || d.rate < minRatePoint.rate) minRatePoint = d;
  }

  return {
    chartData,
    totalAttended,
    avgAttendanceRate,
    avgAttendanceCount,
    countedBuckets: counted,
    maxRatePoint: maxRatePoint ? { bucket: maxRatePoint.bucket, rate: maxRatePoint.rate, attended: maxRatePoint.attended, validMembers: maxRatePoint.validMembers } : null,
    minRatePoint: minRatePoint ? { bucket: minRatePoint.bucket, rate: minRatePoint.rate, attended: minRatePoint.attended, validMembers: minRatePoint.validMembers } : null,
    // 하위 호환: 기존 호출자가 활용했을 가능성 (current snapshot active member count)
    activeMemberCount: memberItems.filter(m => {
      const usages = Array.isArray(m.usages) ? m.usages : [];
      if (usages.length === 0) return false;
      return usages.some(u => u.endDate && moment(u.endDate).isSameOrAfter(periodEnd, 'day'));
    }).length,
  };
}

// ────────────────────────────────────────────────
// aggregateEngagement — show_engagement_chart (연령대 × 회원권 분석)
// ────────────────────────────────────────────────
//
// 입력:
//   engagementItems: CHECK_MEMBER+ENGAGEMENT_LIST_BY+PERIOD buffer.items
//     [{ userId, birthDay, membershipId, membershipName, reserveCount }]
//   memberItems: CHECK_MEMBER+MEMBERSHIP+HISTORY_LIST buffer.items (uniqueMemberCount 산정용)
//
// 출처: 통계 페이지 EngagementChart 로직 reuse — birthDay 기반 연령대 분류 + membershipId 집계.
const AGE_BUCKETS = ['10대', '20대', '30대', '40대', '50대+'];
function _ageBucket(birthDay) {
  if (!birthDay) return null;
  const year = parseInt(String(birthDay).slice(0, 4), 10);
  if (isNaN(year)) return null;
  const age = new Date().getFullYear() - year;
  if (age < 20) return '10대';
  if (age < 30) return '20대';
  if (age < 40) return '30대';
  if (age < 50) return '40대';
  return '50대+';
}

export function aggregateEngagement({ engagementItems, memberItems }) {
  if (!Array.isArray(engagementItems)) engagementItems = [];
  if (!Array.isArray(memberItems)) memberItems = [];

  // Phase 0 — memberItems usages 기반 연령대별 회원권 보유 집계 (unique member count per membership) //
  const byAge = new Map();

  for (const b of AGE_BUCKETS) {
    byAge.set(b, {
      uniqueMembers: new Set(),
      totalReserveCount: 0,
      byMembership: new Map(),
      membershipHolders: new Map(),
    });
  }

  for (const m of memberItems) {
    if (!m || !m.birthDay || !Array.isArray(m.usages)) continue;

    const bucket = _ageBucket(m.birthDay);

    if (!bucket) continue;

    const slot = byAge.get(bucket);
    slot.uniqueMembers.add(m.memberId);

    for (const u of m.usages) {
      if (!u.memberShipId || !u.memberShipName) continue;

      const memName = u.memberShipName;

      if (!slot.membershipHolders.has(memName)) {
        slot.membershipHolders.set(memName, new Set());
      }

      slot.membershipHolders.get(memName).add(m.memberId);

      if (!slot.byMembership.has(memName)) {
        slot.byMembership.set(memName, 0);
      }
    }
  }

  // Phase 1 — engagementItems reserve 사용 실적 누적 //
  for (const e of engagementItems) {
    if (!e || !e.userId) continue;

    const bucket = _ageBucket(e.birthDay);

    if (!bucket) continue;

    const slot = byAge.get(bucket);
    slot.totalReserveCount += e.reserveCount || 0;

    const memKey = e.membershipName || e.membershipId || '(unknown)';
    slot.byMembership.set(memKey, (slot.byMembership.get(memKey) || 0) + (e.reserveCount || 0));
  }

  // 전체 유효회원 (memberItems 기준) //
  const totalActiveMembers = memberItems.length;

  // Phase 2 — 결과 직렬화 (topMembership = 보유 회원 수 기준) //
  const ageDistribution = AGE_BUCKETS.map(bucket => {
    const slot = byAge.get(bucket);

    const topByHolders = [...slot.membershipHolders.entries()]
      .sort((a, b) => b[1].size - a[1].size)[0];

    const topByReserve = [...slot.byMembership.entries()]
      .sort((a, b) => b[1] - a[1])[0];

    const topMembershipName = topByHolders ? topByHolders[0] : (topByReserve ? topByReserve[0] : null);
    const topMembershipHolderCount = topByHolders ? topByHolders[1].size : 0;
    const topMembershipReserveCount = topByReserve ? topByReserve[1] : 0;

    return {
      bucket,
      uniqueMemberCount: slot.uniqueMembers.size,
      totalReserveCount: slot.totalReserveCount,
      ratio: totalActiveMembers > 0 ? Math.round((slot.uniqueMembers.size / totalActiveMembers) * 1000) / 10 : 0,
      topMembershipName,
      topMembershipHolderCount,
      topMembershipReserveCount,
      memberships: [...slot.membershipHolders.entries()]
        .map(([name, holders]) => ({
          name,
          holderCount: holders.size,
          reserveCount: slot.byMembership.get(name) || 0,
        }))
        .sort((a, b) => b.holderCount - a.holderCount),
    };
  });

  return { ageDistribution, totalActiveMembers };
}

// ────────────────────────────────────────────────
// computeLtv — show_ltv (고객 생애 가치)
// ────────────────────────────────────────────────
//
// 입력:
//   salesItems: CHECK_SALES_LIST_BY+YEAR buffer.items ([{ approvalDate, totalPrice }])
//   durationItems: CHECK_MEMBERSHIP+DURATION_LIST_BY+PERIOD buffer.items ([{ memberId, duration }])
//   memberItems: CHECK_MEMBER+MEMBERSHIP+HISTORY_LIST buffer.items (유효 회원 산정용)
//   year: 산정 기준 연도
//
// LTV 계산:
//   priceAvg = YTD 매출 합계 / 누적 월수
//   durationAvg = 평균 회원권 지속 기간 (duration<1은 1로 보정)
//   activeMembers = 현재 유효 회원수
//   LTV = priceAvg × durationAvg / activeMembers
// 출처: 통계 페이지 LTVMainContent 로직 reuse.
export function computeLtv({ salesItems, durationItems, memberItems, year, periodEndISO }) {
  if (!Array.isArray(salesItems)) salesItems = [];
  if (!Array.isArray(durationItems)) durationItems = [];
  if (!Array.isArray(memberItems)) memberItems = [];

  const currentYear = year || new Date().getFullYear();
  const periodEnd = moment(periodEndISO);

  // YTD 매출 합계 + 누적 월수
  let ytdRevenue = 0;
  const monthsSet = new Set();

  for (const s of salesItems) {
    if (!s.approvalDate) continue;
    const ymd = s.approvalDate.split('T')[0];
    const [y, m] = ymd.split('-');
    if (parseInt(y, 10) !== currentYear) continue;
    ytdRevenue += s.totalPrice || 0;
    monthsSet.add(m);
  }
  const monthsCount = Math.max(monthsSet.size, 1);
  const priceAvg = Math.round(ytdRevenue / monthsCount);

  // durationAvg — duration<1은 1로 보정
  const durations = durationItems.map(d => Math.max(d.duration || 0, 1));
  const durationAvg = durations.length > 0 ? Math.round((durations.reduce((a, b) => a + b, 0) / durations.length) * 10) / 10 : 1;

  // 유효 회원 수 (periodEnd 기준)
  const activeMembers = memberItems.filter(m => {
    const usages = Array.isArray(m.usages) ? m.usages : [];
    if (usages.length === 0) return false;
    const last = usages[usages.length - 1];
    return last.endDate && moment(last.endDate).isSameOrAfter(periodEnd, 'day');
  }).length;

  // LTV = priceAvg × durationAvg / activeMembers
  const ltv = activeMembers > 0 ? Math.round((priceAvg * durationAvg) / activeMembers) : 0;

  return { ltv, priceAvg, durationAvg, activeMembers, ytdRevenue, monthsCount };
}
